/**
 * audio_player.ts
 *
 * WebAudio playback for the relay's G.711 audio stream (MSG_AUDIO_INIT 0x03 /
 * MSG_AUDIO_FRAME 0x04). See `handoff/HANDOFF-web-player-audio.md` §4.
 *
 * Shape, and why:
 *
 *  - **One page-global `AudioContext`.** Chrome has historically capped
 *    AudioContexts per document (~6) and throws past it; the CMS renders up to
 *    16 live tiles. One context, one `AudioWorkletNode` + `GainNode` per
 *    audible tile, and only one tile audible at a time.
 *
 *  - **Decoding happens inside the worklet.** The raw G.711 payload is posted
 *    across; the render thread drains the port. This app's main thread is
 *    routinely blocked (a GPU→CPU `drawImage` readback at 25 fps per tile, plus
 *    a `JSON.stringify` + React re-render on every media-state change), so a
 *    main-thread stall must delay *delivery* — absorbed by the ring cushion —
 *    and never *rendering*.
 *
 *  - **We own the resampler, and it is stateful across frame boundaries.**
 *    48000/8000 = 6 exactly, but 44100/8000 = 5.5125 (Apple, some Windows). The
 *    ring holds decoded samples at the *source* rate and `process()` reads it
 *    with a fractional, variable step. That variable step is also the drift
 *    control actuator, which is why `new AudioContext({sampleRate: 8000})` is
 *    deliberately NOT requested: it would hand the resample ratio to the OS.
 *
 *  - **Drift control.** Device codec oscillator vs host audio hardware, ±50 ppm
 *    each → ~100 ppm = 0.36 s/hour. A fixed 120 ms cushion underruns in ~20
 *    minutes without correction. The read step is steered from an EMA of ring
 *    occupancy, clamped to ±0.2 % — 20× the worst crystal error and far below
 *    the ~0.3–0.5 % pitch JND. Same trick the video jitter buffer plays on its
 *    playhead (`jitter_buffer.ts:69-71`, `:170-177`).
 *
 *  - **The worklet module is loaded from a Blob URL.** `addModule()` takes a
 *    URL; both consumers are CRA 4 / webpack 4, which has no
 *    `new URL(..., import.meta.url)` asset support, and `tsup.config.ts` is a
 *    plain library build with no asset pipeline. Shipping a file the host app
 *    must copy into `public/static/` (as `h265web_wasm.js` is today) creates a
 *    deploy coupling that breaks silently. This requires `script-src blob:` in
 *    the production CSP; if it is missing, `addModule` rejects and audio is
 *    reported unavailable with a specific message rather than throwing.
 *
 *  - **No `SharedArrayBuffer`.** COOP/COEP are set on the dev server only
 *    (`example/config-overrides.js:45-58`); the production image sets no such
 *    headers. `postMessage` at 25 msg/s costs nothing.
 */

import { ALAW_TABLE, ULAW_TABLE, g711CodecName, type G711CodecName } from './g711_decoder';

/** Ring capacity, in seconds of source audio. Must comfortably exceed
 *  TARGET_MAX_MS plus the overrun margin (now 1200 + 600 = 1800 ms). */
const RING_SECONDS = 3;

/**
 * Ring occupancy target — adaptive, not a constant.
 *
 * A fixed 120 ms (3 device frames) was the design's starting guess and it is
 * too thin for a real WAN path. Measured against production over 60 s: the wire
 * itself was clean (a Node subscriber saw 25.0 frames/s with zero PTS gaps) but
 * delivery to the browser arrives in bursts — instantaneous rate p10 22.7/s,
 * dipping to 11.8/s — and the ring sat below 20 ms for 99 of 232 samples, i.e.
 * 43 % of the time one bad inter-arrival away from silence. The drift
 * controller cannot rescue that: it has ±0.2 % of authority and was pinned at
 * its negative clamp the whole run.
 *
 * So the cushion grows when it is proven insufficient and decays back when it
 * is proven excessive, exactly as the video jitter buffer re-derives its own
 * target (`jitter_buffer.ts`).
 *
 * Latency is close to free here, and is in fact the RIGHT direction: video is
 * buffered 500–1240 ms and then played on a variable-rate playhead, so it sits
 * 0.6–1.5 s behind the wire while free-running audio lands near 200 ms. Audio
 * therefore LEADS video by roughly a second, and every millisecond added to
 * this cushion moves the two closer together rather than further apart
 * (HANDOFF-web-player-audio.md §7.1: "delay audio to meet video, never the
 * reverse... lengthening the audio ring is free and invisible").
 */
// 300 ms is the measured equilibrium of a real production path, not a guess:
// over a 3-minute live run the ring settled at 236–430 ms, and the single
// underrun in that run happened while it was still at the old starting depth.
// Starting at the equilibrium skips paying for that first glitch, and the ring
// sheds the difference on a path that does not need it.
const TARGET_INITIAL_MS = 300;
const TARGET_MIN_MS = 120;
/**
 * Ceiling on the cushion.
 *
 * Was 600, which measurement showed is below what a real path demands: over a
 * 300 s production capture the delivery curve's peak-to-trough excursion was
 * 675 ms, so **no** cushion under ~640 ms can avoid running the ring dry, and
 * 600 made the "zero underruns in sixty minutes" acceptance criterion
 * unreachable by construction rather than by bad luck.
 *
 * Raising it is close to free and points the right way: video is buffered
 * 500–1240 ms and then played on a variable-rate playhead, so it sits
 * 0.6–1.5 s behind the wire while audio free-runs. Audio therefore LEADS video,
 * and depth spent here closes that gap instead of widening it (§7.1). The cost
 * is time-to-first-sound after an unmute, which is bounded by the CURRENT
 * target — reached only on a path that has already produced gaps that large.
 */
const TARGET_MAX_MS = 1200;
/** Added on each underrun. One starvation is evidence the cushion is short. */
const TARGET_GROW_MS = 80;
/** Shed after a quiet spell, so a single bad minute does not cost latency forever. */
const TARGET_DECAY_MS = 20;
const TARGET_DECAY_QUIET_S = 15;
/**
 * Headroom above target before the oldest audio is dropped.
 *
 * Was 300, and that was too tight to survive the recovery it exists for: a
 * delivery gap is followed by the backlog arriving in a burst, so the ring
 * refills past `target + margin` and trims — discarding precisely the audio it
 * had just waited for. Replaying a measured 300 s trace, 300 ms produced 4
 * overruns and 600 ms produced none, with no other change.
 */
const OVERRUN_MARGIN_MS = 600;
/**
 * How long an observed delivery gap keeps holding the cushion up.
 *
 * Long enough that the gaps on a bursty path (tens of seconds apart, measured)
 * reinforce each other rather than being forgotten between events, short enough
 * that a network that genuinely improves gets its latency back within a couple
 * of minutes.
 */
const GAP_PEAK_HALF_LIFE_MS = 120_000;
/**
 * Headroom over the worst gap actually seen.
 *
 * A purely reactive cushion still pays one dropout for every NEW worst gap,
 * because it can only cover what it has already been hurt by. Sizing above the
 * observed peak buys the next, slightly worse gap. Swept against a measured
 * trace: 1.0 → 3 underruns, 1.25 → 1, 1.75 → 1 but at 1165 ms of latency.
 * 1.25 is where the curve flattens.
 */
const GAP_SAFETY_FACTOR = 1.25;
/** Hard clamp on the resampler ratio trim used for drift correction. */
const DRIFT_CLAMP = 0.002;
/** Proportional gain: full ±0.2 % authority at ±50 % deviation from target. */
const DRIFT_GAIN = 0.004;
/** Occupancy EMA time constant, seconds. */
const DRIFT_EMA_SECONDS = 10;
/** Default rate assumed when a 0x04 arrives before any 0x03 (§1). */
const DEFAULT_SAMPLE_RATE = 8000;

const PROCESSOR_NAME = 'vgps-g711-audio';

/**
 * AudioWorklet processor source. Runs in a separate realm and closes over
 * nothing — every parameter arrives via `processorOptions` or `port`.
 *
 * The G.711 tables are transferred in rather than rebuilt here, so there is
 * exactly one copy of the ITU tables in the codebase (`g711_decoder.ts`).
 */
const PROCESSOR_SRC = String.raw`
class VgpsG711Processor extends AudioWorkletProcessor {
  constructor(options) {
    super();
    const o = (options && options.processorOptions) || {};
    this.alaw = o.alaw;
    this.ulaw = o.ulaw;
    this.ctxRate = sampleRate;
    this.targetMs = o.targetMs;
    this.targetMinMs = o.targetMinMs;
    this.targetMaxMs = o.targetMaxMs;
    this.growMs = o.growMs;
    this.decayMs = o.decayMs;
    this.decayQuietS = o.decayQuietS;
    this.overrunMarginMs = o.overrunMarginMs;
    this.driftClamp = o.driftClamp;
    this.driftGain = o.driftGain;
    this.ringSeconds = o.ringSeconds;
    this.emaSeconds = o.emaSeconds;

    this.underruns = 0;
    this.overruns = 0;
    // Render quanta elapsed — the worklet's own clock. 'currentTime' would do,
    // but it is one more global the offline harness would have to stub.
    this.blocks = 0;
    // Decaying peak of observed delivery gaps, and the cushion depth it implies.
    // Deliberately NOT reset by _resetRing: a flush changes what is in the ring,
    // not what the network is doing.
    this.gapHalfLifeMs = o.gapHalfLifeMs > 0 ? o.gapHalfLifeMs : 120000;
    this.gapSafety = o.gapSafety > 0 ? o.gapSafety : 1;
    this.gapPeakMs = 0;
    this.jitterFloorMs = 0;
    // Set by a 'close' message. 'process()' must then return false so the
    // browser can retire the processor; returning true forever keeps it alive
    // regardless of references, which is how a page accumulates zombies.
    this.closed = false;
    this.configured = false;
    this._configure(o.srcRate, o.codec);

    this.statsCountdown = 0;
    this.port.onmessage = (e) => this._onMessage(e.data);
  }

  // Sizing and all rate-derived state. Called on construction and whenever
  // MSG_AUDIO_INIT announces genuinely different parameters.
  _configure(srcRate, codec) {
    this.srcRate = srcRate > 0 ? srcRate : 8000;
    this.codec = codec | 0;
    this.table = this.codec === 1 ? this.ulaw : this.alaw;

    this.cap = Math.max(1, Math.ceil(this.srcRate * this.ringSeconds));
    this.buf = new Float32Array(this.cap);
    this._applyTarget(this.targetMs);
    this.quietBlocks = 0;

    // Nominal source samples consumed per output sample.
    this.nominalStep = this.srcRate / this.ctxRate;
    this.step = this.nominalStep;

    // EMA coefficient for one 128-sample render quantum.
    this.emaAlpha = 128 / Math.max(1, this.emaSeconds * this.ctxRate);

    this._resetRing();
    this.configured = true;
  }

  // Recompute everything sized off the target. Never touches ring contents:
  // growing the cushion mid-stream must not throw away the audio already in it.
  _applyTarget(ms) {
    const clamped = Math.max(this.targetMinMs, Math.min(this.targetMaxMs, ms));
    this.targetMs = clamped;
    this.targetSamples = Math.round((clamped / 1000) * this.srcRate);
    this.overrunSamples = Math.round(((clamped + this.overrunMarginMs) / 1000) * this.srcRate);
  }

  _resetRing() {
    this.written = 0;
    this.readPos = 0;
    this.refilling = true;
    this.fillEma = this.targetSamples;
    this.lastFramePtsUs = 0;
    this.lastFrameStartSample = 0;
    this.havePts = false;
    // Forget WHEN the last frame arrived, never how bursty the path is. Without
    // this, unmuting after five minutes of silence would measure a five-minute
    // "gap" and slam the cushion straight to its ceiling.
    this.lastArrivalMs = -1;
  }

  _fill() {
    return this.written - Math.floor(this.readPos);
  }

  // Size the cushion from OBSERVED delivery gaps, not only from underruns.
  //
  // Growing on underrun alone charges every path change to the user in audible
  // dropouts: the ring has to actually run dry, once per +80 ms, before the
  // cushion is deep enough. Measured against production over 300 s, delivery
  // gaps reached 643 ms while the cushion started at 300 and the quiet-decay
  // walked it back down between events — 4 dropouts in 5 minutes, ~48/hour,
  // against an acceptance criterion of ZERO in sixty.
  //
  // A gap is evidence in its own right, and it is available the moment the late
  // frame lands rather than after the damage. Holding a decaying peak of them
  // does two jobs: it grows the cushion on the first bad gap, and it stops the
  // decay from shedding depth this path has recently proven it needs.
  //
  // The worklet cannot tell a network gap from a main-thread stall — both reach
  // it as "the next frame is late" — and it does not need to. One mechanism
  // covers both, which is why this lives here and not in the video path.
  _noteArrival(frameSamples) {
    const nowMs = (this.blocks * 128 * 1000) / this.ctxRate;
    if (this.lastArrivalMs >= 0) {
      const gapMs = nowMs - this.lastArrivalMs;
      const decayed = this.gapPeakMs * Math.pow(0.5, gapMs / this.gapHalfLifeMs);
      this.gapPeakMs = gapMs > decayed ? gapMs : decayed;
      // One frame of margin: the gap drains the ring, and the frame that ends
      // it only refills a frame's worth.
      this.jitterFloorMs =
        this.gapPeakMs * this.gapSafety + (frameSamples / this.srcRate) * 1000;
      if (this.jitterFloorMs > this.targetMs) {
        this._applyTarget(this.jitterFloorMs);
        this.quietBlocks = 0;
        this.port.postMessage({ t: 'target', targetMs: this.targetMs, reason: 'jitter' });
      }
    }
    this.lastArrivalMs = nowMs;
  }

  _onMessage(msg) {
    if (!msg) return;
    switch (msg.t) {
      case 'cfg': {
        // Idempotent by contract: 0x03 is re-sent every ~20 s and replayed to
        // every late subscriber, so reconfiguring on each one would tear the
        // audio graph down four times a minute.
        if (this.configured && msg.srcRate === this.srcRate && (msg.codec | 0) === this.codec) return;
        this._configure(msg.srcRate, msg.codec);
        this.port.postMessage({ t: 'reconfigured', srcRate: this.srcRate, codec: this.codec });
        break;
      }
      case 'frame':
        this._writeFrame(msg.payload, msg.ptsUs, msg.silenceSamples | 0);
        break;
      case 'flush':
        this._resetRing();
        break;
      case 'close':
        // Let 'process()' return false so the browser can retire this
        // processor. See 'this.closed'.
        this.closed = true;
        break;
      default:
        break;
    }
  }

  // Decode one G.711 frame straight into the ring. Zero allocation.
  _writeFrame(payloadBuf, ptsUs, silenceSamples) {
    const bytes = new Uint8Array(payloadBuf);
    this._noteArrival(bytes.length);
    const table = this.table;
    const buf = this.buf;
    const cap = this.cap;

    // A measured gap becomes measured silence, so speech is not time-compressed.
    for (let i = 0; i < silenceSamples; i++) {
      buf[(this.written + i) % cap] = 0;
    }
    this.written += silenceSamples;

    this.lastFramePtsUs = ptsUs;
    this.lastFrameStartSample = this.written;
    this.havePts = true;

    const n = bytes.length;
    let w = this.written;
    for (let i = 0; i < n; i++) {
      buf[w % cap] = table[bytes[i]] / 32768;
      w++;
    }
    this.written = w;

    // Overrun: the producer is ahead of the consumer beyond any cushion we are
    // willing to pay latency for. Drop the oldest audio, not the newest.
    if (this._fill() > this.overrunSamples) {
      this.readPos = this.written - this.targetSamples;
      this.overruns++;
      this.port.postMessage({ t: 'overrun' });
    }
  }

  // Drift control: steer the resample ratio from smoothed occupancy. Runs on
  // every render quantum including starved ones, so the reported EMA is a true
  // picture of the cushion rather than only its healthy moments.
  _updateControl() {
    const fill = this._fill();
    this.fillEma += (fill - this.fillEma) * this.emaAlpha;
    let trim = this.driftGain * ((this.fillEma - this.targetSamples) / this.targetSamples);
    if (trim > this.driftClamp) trim = this.driftClamp;
    else if (trim < -this.driftClamp) trim = -this.driftClamp;
    this.step = this.nominalStep * (1 + trim);
    return { fill: fill, trim: trim };
  }

  // ~4 Hz stats. Posted on the starved path too: a stats stream that stops
  // exactly when the ring runs dry would freeze the host's panel at the last
  // healthy values and never surface the underrun that caused it.
  _maybePostStats(fill, trim) {
    if (--this.statsCountdown > 0) return;
    this.statsCountdown = Math.max(1, Math.round(this.ctxRate / 128 / 4));
    const occupancyMs = (fill / this.srcRate) * 1000;
    // PTS of the sample currently leaving the ring, interpolated from the last
    // frame written. Used for the audio↔video skew readout.
    const outputPtsUs = this.havePts
      ? this.lastFramePtsUs + ((this.readPos - this.lastFrameStartSample) / this.srcRate) * 1e6
      : 0;
    this.port.postMessage({
      t: 'stats',
      occupancyMs: occupancyMs,
      occupancyEmaMs: (this.fillEma / this.srcRate) * 1000,
      underruns: this.underruns,
      overruns: this.overruns,
      refilling: this.refilling,
      targetMs: this.targetMs,
      driftPpm: Math.round(trim * 1e6),
      outputPtsUs: outputPtsUs,
      srcRate: this.srcRate,
      codec: this.codec,
    });
  }

  process(_inputs, outputs) {
    // Retired by destroy(). Returning false here is the only way the browser is
    // allowed to collect this processor: a 'process()' that always returns true
    // keeps the node alive even with no references to it, so every quality
    // switch and every unmount would strand one on the page-global context.
    if (this.closed) return false;
    this.blocks++;
    const out = outputs[0];
    if (!out || out.length === 0) return true;
    const ch0 = out[0];
    const n = ch0.length;

    // Underrun hysteresis: once starved, require the FULL target again before
    // emitting samples. Without this you get a machine-gun of 2.67 ms dropouts
    // instead of one clean gap.
    if (this.refilling) {
      if (this._fill() >= this.targetSamples) this.refilling = false;
      else {
        ch0.fill(0);
        for (let c = 1; c < out.length; c++) out[c].set(ch0);
        const starved = this._updateControl();
        this._maybePostStats(starved.fill, starved.trim);
        return true;
      }
    }

    const buf = this.buf;
    const cap = this.cap;
    let pos = this.readPos;
    let i = 0;
    for (; i < n; i++) {
      const i0 = Math.floor(pos);
      // Linear interpolation needs the sample after i0 as well.
      if (this.written - i0 < 2) {
        this.underruns++;
        this.refilling = true;
        // The cushion was demonstrably too small. Grow it, and refill to the
        // NEW target — refilling to the old one would starve again shortly.
        this._applyTarget(this.targetMs + this.growMs);
        this.quietBlocks = 0;
        this.port.postMessage({ t: 'target', targetMs: this.targetMs, reason: 'underrun' });
        for (; i < n; i++) ch0[i] = 0;
        break;
      }
      const frac = pos - i0;
      const s0 = buf[i0 % cap];
      const s1 = buf[(i0 + 1) % cap];
      ch0[i] = s0 + (s1 - s0) * frac;
      pos += this.step;
    }
    this.readPos = pos;

    for (let c = 1; c < out.length; c++) out[c].set(ch0);

    const control = this._updateControl();

    // Decay: a long quiet spell is evidence the cushion is bigger than this
    // path needs, and latency should not be paid forever for one bad minute.
    // Only shed while genuinely comfortable — shedding during a dip would
    // re-create the starvation that grew it.
    //
    // Two guards, and both are load-bearing:
    //
    //  - The floor. Never shed below the depth the observed delivery gaps say
    //    this path needs, or the decay spends its time undoing what the gaps
    //    just taught. Replaying a measured trace without it: the cushion grew
    //    to 380 on the first dropout, decayed 360 → 340 → 320 over the next
    //    70 s, and was starved again at 79.5 s. It was disarming itself.
    //
    //  - A CONTIGUOUS window. 'quietBlocks' used to survive any quantum that
    //    was neither above target nor refilling, so "15 s quiet" really meant
    //    "15 s of above-target quanta, whenever they happened" — the counter
    //    ratcheted across dips and the cushion shed about twice as fast as
    //    documented.
    const decayFloorMs = this.jitterFloorMs > this.targetMinMs ? this.jitterFloorMs : this.targetMinMs;
    if (this.targetMs > decayFloorMs && !this.refilling && control.fill > this.targetSamples) {
      if (++this.quietBlocks >= (this.decayQuietS * this.ctxRate) / 128) {
        this.quietBlocks = 0;
        const next = this.targetMs - this.decayMs;
        this._applyTarget(next > decayFloorMs ? next : decayFloorMs);
        this.port.postMessage({ t: 'target', targetMs: this.targetMs, reason: 'decay' });
      }
    } else {
      this.quietBlocks = 0;
    }

    this._maybePostStats(control.fill, control.trim);

    return true;
  }
}

registerProcessor('${PROCESSOR_NAME}', VgpsG711Processor);
`;

/** Live playback statistics for one audio stream. */
export interface AudioSinkStats {
  /** Rate announced by MSG_AUDIO_INIT. Never hardcode 8000 — see §7.2. */
  announcedSampleRate: number;
  announcedCodec: G711CodecName | null;
  /** 0x04 messages accepted off the wire since attach. */
  framesReceived: number;
  /** Bytes of audio payload + framing received, for bitrate accounting. */
  bytesReceived: number;
  /** Current ring occupancy, milliseconds. */
  ringOccupancyMs: number;
  /** Smoothed ring occupancy — this is the number that proves drift control works. */
  ringOccupancyEmaMs: number;
  /** Current adaptive cushion target, ms. Grows on underrun, decays when quiet. */
  targetMs: number;
  underruns: number;
  overruns: number;
  /** Resampler trim currently applied, in ppm. */
  driftPpm: number;
  /** PTS of the sample currently leaving the ring (µs), 0 when unknown. */
  outputPtsUs: number;
  /** Whether this stream currently owns page audio output. */
  enabled: boolean;
  /** `AudioContext.state === 'running'`. False ⇒ a user gesture is still required. */
  contextRunning: boolean;
}

/** Emitted whenever the audible state of a stream changes. */
export interface AudioSinkState {
  enabled: boolean;
  contextRunning: boolean;
  announcedSampleRate: number;
  announcedCodec: G711CodecName | null;
  /**
   * Whether ANY audio has arrived on the wire. A milestone, not a count: the
   * count moves 25 times a second and no consumer of this event needs it. The
   * running total stays on `AudioSinkStats.framesReceived` for the stats panel.
   */
  receivingAudio: boolean;
  /**
   * Why audio cannot play here, or null when it can.
   *
   * Set when the audio graph could not be built at all — an insecure origin
   * (AudioWorklet is [SecureContext]-gated), a CSP that blocks `script-src
   * blob:`, a browser without Web Audio. `enabled` is forced back to false
   * when this is set, so a host that only watches `enabled` still cannot show
   * a lit speaker button over silence.
   */
  unavailableReason: string | null;
}

type AudioContextCtor = new (options?: AudioContextOptions) => AudioContext;

function resolveAudioContextCtor(): AudioContextCtor | null {
  const w = globalThis as unknown as {
    AudioContext?: AudioContextCtor;
    webkitAudioContext?: AudioContextCtor;
  };
  return w.AudioContext ?? w.webkitAudioContext ?? null;
}

/**
 * Page-global owner of the single `AudioContext`, the worklet module
 * registration, and exclusive-audio bookkeeping.
 *
 * Keyed on a `globalThis` symbol rather than module scope: if a host app ever
 * resolves two copies of `@vgps/livekit_player` (duplicate bundle), module
 * scope would give each copy its own context and split the "one audible tile"
 * ledger across them, so two tiles would play at once with neither aware of the
 * other. The symbol makes that failure impossible for the price of one lookup.
 */
class AudioHub {
  private ctx: AudioContext | null = null;
  private modulePromise: Promise<void> | null = null;
  private owner: AudioSink | null = null;
  private moduleError: Error | null = null;

  /**
   * The cushion depth the page last had to reach, carried across sinks.
   *
   * A quality switch (101↔102 / 201↔202) and a tab-visibility pause both
   * destroy the sink and build a new one, and `channelId` is in the session
   * effect's dependencies, so this happens on every HD↔SD toggle. Without this
   * the new sink starts back at the default and re-pays, in audible dropouts,
   * everything the old one had already learned. Nothing about the network
   * changed — only which tile is asking.
   *
   * Page-global rather than per-path on purpose: only one tile is audible at a
   * time, so this IS the current path, and carrying a too-deep cushion to a
   * better network is self-correcting (the quiet-decay sheds it) while carrying
   * a too-shallow one is not (it costs a dropout to discover).
   */
  rememberedTargetMs = 0;

  /** Create (or return) the page's AudioContext. Safe to call off-gesture —
   *  the context simply starts `suspended`. */
  ensureContext(): AudioContext | null {
    if (this.ctx) return this.ctx;
    const Ctor = resolveAudioContextCtor();
    if (!Ctor) {
      console.warn('[audio] Web Audio is unavailable in this browser; audio playback disabled');
      return null;
    }
    // Deliberately NOT `{ sampleRate: 8000 }` — see the header comment.
    this.ctx = new Ctor();
    this.ctx.onstatechange = () => {
      // iOS suspends on tab background and on a phone-call interruption, so
      // every sink must re-read state rather than trust its own last click.
      this.owner?._notifyState();
    };
    return this.ctx;
  }

  /**
   * Register the worklet module. Started eagerly as soon as a stream is known
   * to carry audio, so that a later click handler contains only `resume()` and
   * a state flip — an `await addModule(...)` inside the gesture would expire it.
   */
  ensureModule(): Promise<void> {
    if (this.moduleError) return Promise.reject(this.moduleError);
    if (this.modulePromise) return this.modulePromise;

    const ctx = this.ensureContext();
    if (!ctx) {
      this.moduleError = new Error('Web Audio is unavailable in this browser');
      return Promise.reject(this.moduleError);
    }
    if (!ctx.audioWorklet) {
      // `BaseAudioContext.audioWorklet` is [SecureContext]-gated. An
      // AudioContext still CONSTRUCTS and reports 'running' on an insecure
      // origin, so everything looks healthy right up to the point where there
      // is no sound — which is exactly how this was missed: the CMS dev server
      // reached over a LAN IP (http://192.168.31.226:3000) is not a secure
      // context, while http://localhost:3000 is.
      //
      // Name the origin rather than blaming the browser. "AudioWorklet is not
      // supported in this browser" sends someone to check compatibility tables
      // for a browser that supports it perfectly well.
      const insecure = typeof globalThis !== 'undefined' &&
        (globalThis as { isSecureContext?: boolean }).isSecureContext === false;
      const origin = typeof location !== 'undefined' ? location.origin : 'this page';
      this.moduleError = new Error(
        insecure
          ? `AudioWorklet requires a secure context, and ${origin} is not one. ` +
            'Serve the app over HTTPS, or reach it on http://localhost — an IP address over ' +
            'plain HTTP will never produce audio, however healthy the AudioContext looks.'
          : 'AudioWorklet is unavailable on this AudioContext',
      );
      return Promise.reject(this.moduleError);
    }

    const blob = new Blob([PROCESSOR_SRC], { type: 'text/javascript' });
    const url = URL.createObjectURL(blob);
    this.modulePromise = ctx.audioWorklet
      .addModule(url)
      .then(() => {
        URL.revokeObjectURL(url);
      })
      .catch((err: unknown) => {
        URL.revokeObjectURL(url);
        const base = err instanceof Error ? err.message : String(err);
        this.moduleError = new Error(
          `[audio] AudioWorklet module failed to load: ${base}. ` +
            'If this is a production build, check that the deployed CSP allows "script-src blob:".',
        );
        console.error(this.moduleError.message);
        throw this.moduleError;
      });
    return this.modulePromise;
  }

  /** Make `sink` the one audible stream on the page, silencing any other. */
  claim(sink: AudioSink): void {
    if (this.owner && this.owner !== sink) {
      const previous = this.owner;
      this.owner = sink;
      previous._forceDisable();
      return;
    }
    this.owner = sink;
  }

  release(sink: AudioSink): void {
    if (this.owner === sink) this.owner = null;
  }

  get context(): AudioContext | null {
    return this.ctx;
  }

  contextRunning(): boolean {
    return this.ctx?.state === 'running';
  }
}

const HUB_KEY = Symbol.for('vgps.livekit_player.audio_hub.v1');

function hub(): AudioHub {
  const g = globalThis as unknown as Record<symbol, AudioHub | undefined>;
  let existing = g[HUB_KEY];
  if (!existing) {
    existing = new AudioHub();
    g[HUB_KEY] = existing;
  }
  return existing;
}

/** Exposed for tests / diagnostics: is a page AudioContext currently running? */
export function audioContextRunning(): boolean {
  return hub().contextRunning();
}

/**
 * One audio stream's playback path: worklet node → gain → destination.
 *
 * Created lazily by `HybridPlayerAdapter` on the first audio message, so a
 * page full of video-only tiles never constructs an AudioContext and never
 * produces Chrome's "AudioContext was not allowed to start" console warning.
 */
export class AudioSink {
  private node: AudioWorkletNode | null = null;
  private gain: GainNode | null = null;
  private nodePromise: Promise<void> | null = null;
  private destroyed = false;

  private enabled = false;
  private volume = 1;

  private announcedSampleRate = 0;
  private announcedCodec: G711CodecName | null = null;
  private codecByte = 0;
  private sampleRate = DEFAULT_SAMPLE_RATE;
  private configuredOnce = false;
  private warnedMissingInit = false;

  private framesReceived = 0;
  private bytesReceived = 0;

  private ringOccupancyMs = 0;
  private ringOccupancyEmaMs = 0;
  private targetMs = TARGET_INITIAL_MS;
  private underruns = 0;
  private overruns = 0;
  private driftPpm = 0;
  private outputPtsUs = 0;

  private onState: ((state: AudioSinkState) => void) | null = null;
  private lastNotifiedState: AudioSinkState | null = null;
  private unavailableReason: string | null = null;

  constructor(onState?: (state: AudioSinkState) => void, initialTargetMs?: number) {
    this.onState = onState ?? null;
    if (typeof initialTargetMs === 'number' && Number.isFinite(initialTargetMs)) {
      this.targetMs = Math.max(TARGET_MIN_MS, Math.min(TARGET_MAX_MS, initialTargetMs));
    }
    // Start from what the page already learned about this network, if it is
    // deeper than the configured starting point. `max` rather than assignment:
    // an operator who raised REACT_APP_LIVEKIT_AUDIO_TARGET_MS meant it as a
    // floor, and a previous stream's shallower equilibrium must not undo that.
    const remembered = hub().rememberedTargetMs;
    if (remembered > this.targetMs) {
      this.targetMs = Math.min(TARGET_MAX_MS, remembered);
    }
    // Begin module registration now: by the time the user clicks the speaker,
    // `addModule` has long resolved and the click handler stays synchronous.
    // Registering the module now does double duty: by the time the user clicks
    // the speaker `addModule` has long resolved so the click handler stays
    // synchronous, AND a hard failure (insecure origin, blocked CSP) is known
    // before the user ever clicks, so the control can be disabled with a reason
    // instead of accepting a click it cannot honour.
    void hub()
      .ensureModule()
      .catch((err: unknown) => {
        this._markUnavailable(err);
      });
  }

  /**
   * Apply a MSG_AUDIO_INIT. Idempotent: the relay re-sends 0x03 every ~500
   * audio packets (≈20 s) and replays it to every late subscriber, so this
   * must reconfigure only on a genuine parameter change.
   */
  configure(codec: number, sampleRate: number, _channels: number): void {
    // The announced rate is an unvalidated u32 off the wire. A corrupt one
    // reaches `_configure`, sizes the ring from it, and can leave the processor
    // emitting NaN every quantum — with no `processorerror` handler to notice.
    // Range rather than a specific value: the contract says never hardcode 8000
    // (§7.2), only that it must be a sample rate.
    const plausible = sampleRate >= 4000 && sampleRate <= 96000;
    if (sampleRate > 0 && !plausible) {
      console.warn(
        `[audio] MSG_AUDIO_INIT announced ${sampleRate} Hz, which is not a plausible ` +
          `sample rate; falling back to ${DEFAULT_SAMPLE_RATE} Hz`,
      );
    }
    const rate = plausible ? sampleRate : DEFAULT_SAMPLE_RATE;
    const changed = !this.configuredOnce || rate !== this.sampleRate || codec !== this.codecByte;

    this.announcedSampleRate = rate;
    this.announcedCodec = g711CodecName(codec);
    this.configuredOnce = true;

    if (!changed) return;

    this.sampleRate = rate;
    this.codecByte = codec;
    this.node?.port.postMessage({ t: 'cfg', srcRate: rate, codec });
    this._notifyState();
  }

  /**
   * Feed one MSG_AUDIO_FRAME payload.
   *
   * `silenceSamples` lets the caller convert a measured PTS gap into measured
   * silence rather than time-compressed speech.
   */
  pushFrame(payload: Uint8Array, ptsUs: number, silenceSamples = 0): void {
    if (this.destroyed) return;
    this.framesReceived++;
    this.bytesReceived += payload.byteLength;

    if (!this.configuredOnce && !this.warnedMissingInit) {
      this.warnedMissingInit = true;
      console.warn(
        `[audio] MSG_AUDIO_FRAME arrived before any MSG_AUDIO_INIT; ` +
          `assuming ${DEFAULT_SAMPLE_RATE} Hz A-law until 0x03 arrives`,
      );
    }
    // Announce the 0 → 1 transition, and only that one. This is the signal a
    // host needs to tell "advertised audio, none delivered" apart from audio
    // that is simply playing; without it a tile that IS receiving audio still
    // reports receivingAudio: false forever, because nothing else calls
    // _notifyState once playback is steady, and the UI accuses the device of
    // sending nothing while the user is listening to it. Emitting per frame
    // instead would put 25 events/s on the SDK bus and re-render every tile.
    if (this.framesReceived === 1) this._notifyState();
    if (!this.enabled || !this.node) return;

    // Copy out of the socket buffer: the payload is a view over the WebSocket
    // message and postMessage's structured clone must not race the next frame.
    const copy = new Uint8Array(payload.length);
    copy.set(payload);
    this.node.port.postMessage(
      { t: 'frame', payload: copy.buffer, ptsUs, silenceSamples },
      [copy.buffer],
    );
  }

  /** Drop everything buffered and restart from live. */
  flush(): void {
    this.node?.port.postMessage({ t: 'flush' });
  }

  /** Nominal samples in one millisecond of source audio — for gap→silence maths. */
  samplesPerMs(): number {
    return this.sampleRate / 1000;
  }

  /**
   * Enable or disable audible output for this stream.
   *
   * MUST be reachable synchronously from a click handler: `ctx.resume()` is
   * called before any `await`, or the gesture has already expired by the time
   * it runs (and on Safari it then fails silently).
   */
  setEnabled(enabled: boolean): void {
    if (this.destroyed || enabled === this.enabled) {
      if (!this.destroyed) this._notifyState();
      return;
    }

    if (!enabled) {
      this.enabled = false;
      hub().release(this);
      if (this.gain) this.gain.gain.value = 0;
      // Muting stops `pushFrame` feeding the worklet, but the worklet keeps
      // rendering — so without this the ring starves a few hundred ms later and
      // books a spurious underrun, which grows the cushion for a "problem" that
      // was the user clicking mute. It also poisoned the very counter used to
      // diagnose real dropouts. Flushing parks the ring in `refilling`, where
      // no underrun is charged and none is deserved.
      this.flush();
      this._notifyState();
      return;
    }

    // Already known to be impossible here. Do not claim exclusive audio and do
    // not report enabled — that would silence whichever tile IS audible and
    // light this one's button over nothing.
    if (this.unavailableReason) {
      this._notifyState();
      return;
    }

    const ctx = hub().ensureContext();
    if (!ctx) {
      this._markUnavailable(new Error('Web Audio is unavailable in this browser'));
      return;
    }
    // Synchronous, inside the gesture. Everything below may be async.
    void ctx.resume();

    this.enabled = true;
    hub().claim(this);
    void this._ensureNode().then(() => {
      if (this.destroyed || !this.enabled) return;
      if (!this.node || !this.gain) {
        // The graph did not come up. Without this the sink stays `enabled`
        // forever and the speaker button reports playing audio over silence,
        // which is worse than an error: the user has no reason to look further.
        this._markUnavailable(new Error('the audio graph could not be built'));
        return;
      }
      this.gain.gain.value = this.volume;
      // Frames have been arriving the whole time this tile was muted. Playing
      // that backlog would start the user a minute behind the picture.
      this.flush();
      this._notifyState();
    });
    this._notifyState();
  }

  /** Record that audio cannot play here, drop any claim, and tell the host. */
  private _markUnavailable(err: unknown): void {
    if (this.destroyed) return;
    this.unavailableReason = err instanceof Error ? err.message : String(err);
    this.enabled = false;
    hub().release(this);
    if (this.gain) this.gain.gain.value = 0;
    this._notifyState();
  }

  setVolume(volume: number): void {
    const clamped = Math.max(0, Math.min(1, volume));
    this.volume = clamped;
    if (this.gain && this.enabled) this.gain.gain.value = clamped;
    this._notifyState();
  }

  /** Called by the hub when another tile takes audio ownership. */
  _forceDisable(): void {
    if (!this.enabled) return;
    this.enabled = false;
    if (this.gain) this.gain.gain.value = 0;
    this._notifyState();
  }

  /**
   * Publish the audible state, but only when it has actually changed.
   *
   * Several paths call this for the same resulting state — enabling touches
   * volume, ownership and the context, and `setVolume` changes nothing that
   * appears in this payload at all. Every emission crosses the SdkEvent bus and
   * lands in a React setState on the tile, so a volume slider would otherwise
   * put a re-render on the page for every pixel it moves.
   */
  _notifyState(): void {
    if (!this.onState || this.destroyed) return;
    const next: AudioSinkState = {
      enabled: this.enabled,
      contextRunning: hub().contextRunning(),
      announcedSampleRate: this.announcedSampleRate,
      announcedCodec: this.announcedCodec,
      receivingAudio: this.framesReceived > 0,
      unavailableReason: this.unavailableReason,
    };
    const prev = this.lastNotifiedState;
    if (
      prev &&
      prev.enabled === next.enabled &&
      prev.contextRunning === next.contextRunning &&
      prev.announcedSampleRate === next.announcedSampleRate &&
      prev.announcedCodec === next.announcedCodec &&
      prev.receivingAudio === next.receivingAudio &&
      prev.unavailableReason === next.unavailableReason
    ) {
      return;
    }
    this.lastNotifiedState = next;
    this.onState(next);
  }

  getStats(): AudioSinkStats {
    return {
      announcedSampleRate: this.announcedSampleRate,
      announcedCodec: this.announcedCodec,
      framesReceived: this.framesReceived,
      bytesReceived: this.bytesReceived,
      ringOccupancyMs: this.ringOccupancyMs,
      ringOccupancyEmaMs: this.ringOccupancyEmaMs,
      targetMs: this.targetMs,
      underruns: this.underruns,
      overruns: this.overruns,
      driftPpm: this.driftPpm,
      outputPtsUs: this.outputPtsUs,
      enabled: this.enabled,
      contextRunning: hub().contextRunning(),
    };
  }

  /** Tear down the node graph. Must leave zero running AudioWorkletNodes —
   *  Chrome's per-document context cap is reached by leaking these. */
  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    hub().release(this);
    this.enabled = false;
    if (this.node) {
      // Retire the processor. Disconnecting alone is not enough: `process()`
      // returning true keeps a processor alive independently of references, so
      // without this every quality switch and every unmount leaves one running
      // on the page-global AudioContext for the life of the page.
      try {
        this.node.port.postMessage({ t: 'close' });
      } catch {
        /* port already closed */
      }
      this.node.port.onmessage = null;
      try {
        this.node.disconnect();
      } catch {
        /* already disconnected */
      }
      this.node = null;
    }
    if (this.gain) {
      try {
        this.gain.disconnect();
      } catch {
        /* already disconnected */
      }
      this.gain = null;
    }
    this.nodePromise = null;
    this.onState = null;
  }

  private _ensureNode(): Promise<void> {
    if (this.nodePromise) return this.nodePromise;
    this.nodePromise = hub()
      .ensureModule()
      .then(() => {
        if (this.destroyed) return;
        const ctx = hub().context;
        if (!ctx) return;

        const node = new AudioWorkletNode(ctx, PROCESSOR_NAME, {
          numberOfInputs: 0,
          numberOfOutputs: 1,
          outputChannelCount: [1],
          processorOptions: {
            alaw: ALAW_TABLE,
            ulaw: ULAW_TABLE,
            srcRate: this.sampleRate,
            codec: this.codecByte,
            targetMs: this.targetMs,
            targetMinMs: TARGET_MIN_MS,
            targetMaxMs: TARGET_MAX_MS,
            growMs: TARGET_GROW_MS,
            decayMs: TARGET_DECAY_MS,
            decayQuietS: TARGET_DECAY_QUIET_S,
            overrunMarginMs: OVERRUN_MARGIN_MS,
            driftClamp: DRIFT_CLAMP,
            driftGain: DRIFT_GAIN,
            ringSeconds: RING_SECONDS,
            emaSeconds: DRIFT_EMA_SECONDS,
            gapHalfLifeMs: GAP_PEAK_HALF_LIFE_MS,
            gapSafety: GAP_SAFETY_FACTOR,
          },
        });
        node.port.onmessage = (ev: MessageEvent) => this._onWorkletMessage(ev.data);

        const gain = ctx.createGain();
        gain.gain.value = this.enabled ? this.volume : 0;
        node.connect(gain);
        gain.connect(ctx.destination);

        this.node = node;
        this.gain = gain;
      })
      .catch((err: unknown) => {
        this.nodePromise = null;
        console.error('[audio] failed to build the audio graph:', err);
      });
    return this.nodePromise;
  }

  private _onWorkletMessage(msg: { t?: string } & Record<string, unknown>): void {
    if (!msg || this.destroyed) return;
    if (msg.t === 'stats') {
      this.ringOccupancyMs = msg.occupancyMs as number;
      this.ringOccupancyEmaMs = msg.occupancyEmaMs as number;
      this.underruns = msg.underruns as number;
      this.overruns = msg.overruns as number;
      this.driftPpm = msg.driftPpm as number;
      this.outputPtsUs = msg.outputPtsUs as number;
      if (typeof msg.targetMs === 'number') {
        this.targetMs = msg.targetMs;
        hub().rememberedTargetMs = this.targetMs;
      }
    } else if (msg.t === 'target') {
      this.targetMs = msg.targetMs as number;
      // Carried to the next sink this page builds — see AudioHub.
      hub().rememberedTargetMs = this.targetMs;
      const how =
        msg.reason === 'decay' ? 'shed' : msg.reason === 'jitter' ? 'raised (delivery jitter)' : 'grown';
      console.info(`[audio] cushion ${how} to ${Math.round(this.targetMs)} ms`);
    } else if (msg.t === 'reconfigured') {
      console.info(
        `[audio] worklet reconfigured: ${msg.srcRate as number} Hz codec=${msg.codec as number}`,
      );
    }
  }
}
